import { 
  FileSpreadsheet,
  Sparkles,
  FileDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useExportExcel from '@/hooks/useExportExcel';
import { cn } from '@/lib/utils';

import type {
  ICompanyItemGroupedInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';

type Props = {
  items: ICompanyItemGroupedInterface[];
  startDate: Date | null;
  endDate: Date | null;
  selectedCompany: ICompanyList | null;
  allTypes: string[];
};

// ✅ تحديد الأعمدة التي ستظهر بناءً على `allTypes`
const getAllowedColumns = (
  allTypes: string[],
): (keyof ICompanyItemGroupedInterface)[] => {
  const allowedColumns: (keyof ICompanyItemGroupedInterface)[] = [
    'ItemID',
    'ItemName',
    'UnitPrice',
    'Stock',
    'StockValue',
  ];

  if (allTypes.includes(' بيع')) {
    allowedColumns.push('TotalSales', 'SalesValue');
  }
  if (allTypes.includes('شراء')) {
    allowedColumns.push('TotalPurchases');
  }
  if (allTypes.includes('مرتجع مورد')) {
    allowedColumns.push('TotalReturnsSupplier');
  }
  if (allTypes.includes('مرتجع عميل')) {
    allowedColumns.push('TotalReturnsClient');
  }

  return allowedColumns;
};

function ExcelBtn({
  items,
  endDate,
  selectedCompany,
  startDate,
  allTypes,
}: Props) {
  // ✅ استخراج الأعمدة المطلوبة فقط
  const allowedColumns = getAllowedColumns(allTypes);
  const filteredItems = items.map((item) =>
    Object.keys(item)
      .filter((key) =>
        allowedColumns.includes(key as keyof ICompanyItemGroupedInterface),
      )
      .reduce((acc, key) => {
        (acc as Partial<ICompanyItemGroupedInterface>)[
          key as keyof ICompanyItemGroupedInterface
        ] = item[key as keyof ICompanyItemGroupedInterface] as any;
        return acc;
      }, {} as Partial<ICompanyItemGroupedInterface>),
  );

  // ✅ استخدام البيانات المصفاة في التصدير
  const exportToExcel = useExportExcel(
    filteredItems,
    `تقرير مجمع اصناف شركة ${selectedCompany?.Name} من ${startDate?.toLocaleDateString()} الى ${endDate?.toLocaleDateString()}.xlsx`,
  );

  // Calculate statistics
  const totalRows = items.length;
  const totalColumns = allowedColumns.length;
  const totalCells = totalRows * totalColumns;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            size="sm" 
            onClick={exportToExcel}
            className={cn(
              "relative overflow-hidden transition-all duration-300",
              "bg-gradient-to-r from-emerald-600 via-emerald-500 to-green-500",
              "hover:from-emerald-700 hover:via-emerald-600 hover:to-green-600",
              "dark:from-emerald-700 dark:via-emerald-600 dark:to-green-600",
              "dark:hover:from-emerald-800 dark:hover:via-emerald-700 dark:hover:to-green-700",
              "text-white shadow-lg hover:shadow-xl hover:scale-105",
              "border border-white/20 dark:border-emerald-500/30",
              "group"
            )}
          >
            <span className="relative z-10 flex items-center gap-2">
              <div className="relative">
                <FileSpreadsheet className="h-4 w-4 transition-transform group-hover:rotate-12" />
                <Sparkles className="absolute -top-1 -right-1 h-2.5 w-2.5 text-yellow-300 animate-pulse" />
              </div>
              <span className="font-medium">تصدير Excel</span>
              <Badge 
                variant="outline" 
                className="ml-1 h-5 border-white/30 bg-white/20 text-white text-[10px] px-1.5 font-bold"
              >
                {totalRows}
              </Badge>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          </Button>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="max-w-xs p-3 bg-background/95 backdrop-blur-sm border-border/50 shadow-xl dark:bg-slate-900/95 dark:border-slate-700/50"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <div className="p-1.5 rounded-md bg-gradient-to-br from-emerald-600 to-green-600 text-white">
                <FileSpreadsheet className="h-4 w-4" />
              </div>
              <span>تصدير تقرير الإجماليات</span>
            </div>
            
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50 dark:bg-slate-800/50">
                <span className="text-muted-foreground">عدد الأصناف:</span>
                <Badge variant="outline" className="h-5 px-2">
                  {totalRows} صنف
                </Badge>
              </div>
              
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50 dark:bg-slate-800/50">
                <span className="text-muted-foreground">عدد الأعمدة:</span>
                <Badge variant="outline" className="h-5 px-2">
                  {totalColumns} عمود
                </Badge>
              </div>
              
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50 dark:bg-slate-800/50">
                <span className="text-muted-foreground">إجمالي الخلايا:</span>
                <Badge variant="outline" className="h-5 px-2">
                  {totalCells.toLocaleString()}
                </Badge>
              </div>
            </div>
            
            <div className="pt-2 border-t border-border/50 dark:border-slate-700/50">
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                <FileDown className="h-3.5 w-3.5" />
                <span className="font-medium">انقر للتصدير الآن</span>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ExcelBtn;
